
import React, { useEffect } from 'react';
import { CV_DATA, Icons, CONTACT_INFO } from '../constants';

interface CVModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CVModal: React.FC<CVModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handlePrintAction = (e: React.MouseEvent) => {
    // Voorkom dat de klik doorgaat naar de overlay (wat de modal zou sluiten)
    e.preventDefault();
    e.stopPropagation();
    
    // Een kleine vertraging helpt bij browsers die print-dialogen onderdrukken
    // in bepaalde webview- of sandbox-omgevingen.
    setTimeout(() => {
      try {
        window.print();
      } catch (err) {
        console.error("Printen mislukt:", err);
        alert("Printen wordt momenteel niet ondersteund in deze browseromgeving. Probeer het in een standaard browser zoals Chrome of Safari.");
      }
    }, 150);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-8">
      {/* Background overlay - Hidden on print */}
      <div 
        className="absolute inset-0 bg-primary/30 backdrop-blur-md transition-opacity print:hidden cursor-pointer" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal Container */}
      <div className="relative bg-white w-full max-w-4xl max-h-screen md:max-h-[90vh] overflow-hidden md:rounded-[3rem] shadow-2xl border border-accent-light animate-in fade-in zoom-in duration-300 flex flex-col print:shadow-none print:border-none print:max-h-none print:rounded-none print:static print:overflow-visible print:w-full">
        
        {/* Modal Header - UI Version */}
        <div className="p-6 md:p-12 border-b border-accent-light bg-liza-bg/50 flex justify-between items-center print:hidden shrink-0">
          <div>
            <h2 className="text-2xl md:text-3xl font-serif text-primary mb-1 md:mb-2">Curriculum Vitae</h2>
            <p className="text-[9px] md:text-[10px] uppercase tracking-[0.3em] font-bold text-accent">{CV_DATA.name}</p>
          </div>
          <div className="flex gap-2 md:gap-4">
            <button 
              onClick={handlePrintAction}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center border border-accent text-accent hover:bg-primary hover:text-white transition-all duration-300 shadow-sm bg-white cursor-pointer active:scale-90"
              title="Download als PDF (via Print-menu)"
              aria-label="Download als PDF"
            >
              <Icons.Download />
            </button>
            <button 
              onClick={handlePrintAction}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center border border-accent text-accent hover:bg-primary hover:text-white transition-all duration-300 shadow-sm bg-white cursor-pointer active:scale-90"
              title="Print CV"
              aria-label="Print CV"
            >
              <Icons.Printer />
            </button>
            <button 
              onClick={onClose}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center border border-accent hover:bg-primary hover:text-white transition-all duration-300 group shadow-sm bg-white cursor-pointer active:scale-90"
              aria-label="Sluit CV"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Print Header - Only visible when printing */}
        <div className="hidden print:block p-0 mb-12 border-b-2 border-primary/20 pb-8 w-full">
           <div className="flex justify-between items-end">
             <div>
               <h1 className="text-4xl font-serif text-primary mb-2">Curriculum Vitae</h1>
               <p className="text-lg font-serif text-accent italic">{CV_DATA.name}</p>
             </div>
             <div className="text-right text-[10px] text-gray-400 uppercase tracking-widest leading-relaxed">
               {CONTACT_INFO.fullAddress}<br />
               {CONTACT_INFO.phone}<br />
               {CONTACT_INFO.email}
             </div>
           </div>
        </div>

        {/* Content Area */}
        <div id="cv-content" className="flex-grow overflow-y-auto p-8 md:p-12 space-y-16 print:overflow-visible print:p-0 print:space-y-12">
          
          {/* Werkervaring */}
          <section className="print:break-inside-avoid">
            <span className="text-accent font-bold tracking-[0.4em] text-[10px] uppercase mb-8 block border-b border-accent-light pb-2 print:text-primary print:border-primary/10">Werkervaring</span>
            <div className="space-y-10">
              {CV_DATA.experience.map((exp, i) => (
                <div key={i} className="flex flex-col md:flex-row gap-4 md:gap-12 print:flex-row print:gap-8">
                  <div className="w-full md:w-1/4 print:w-1/4">
                    <span className="text-[11px] font-bold text-primary bg-primary/5 px-4 py-1.5 rounded-full whitespace-nowrap print:bg-transparent print:p-0">{exp.period}</span>
                  </div>
                  <div className="w-full md:w-3/4 print:w-3/4">
                    <h4 className="text-lg font-serif text-gray-800 mb-2">{exp.title}</h4>
                    <p className="text-sm text-gray-500 font-light leading-relaxed">{exp.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Opleidingen */}
          <section className="print:break-inside-avoid">
            <span className="text-accent font-bold tracking-[0.4em] text-[10px] uppercase mb-8 block border-b border-accent-light pb-2 print:text-primary print:border-primary/10">Opleidingen</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 print:grid-cols-2">
              {CV_DATA.education.map((edu, i) => (
                <div key={i} className="flex gap-4">
                  <span className="text-[11px] font-serif italic text-primary shrink-0 w-10">{edu.year}</span>
                  <p className="text-sm text-gray-600 font-light leading-snug">{edu.name}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Sterke punten */}
          <section className="print:break-inside-avoid">
            <span className="text-accent font-bold tracking-[0.4em] text-[10px] uppercase mb-6 block print:text-primary">Sterke punten</span>
            <div className="flex flex-wrap gap-2">
              {CV_DATA.strengths.map((strength, i) => (
                <span key={i} className="bg-liza-bg text-primary text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-lg border border-accent-light print:border-primary/10 print:bg-transparent">
                  {strength}
                </span>
              ))}
            </div>
          </section>

          {/* Contact info for Web Only */}
          <section className="print:hidden pt-8 border-t border-accent-light">
            <span className="text-accent font-bold tracking-[0.4em] text-[10px] uppercase mb-6 block">Contactgegevens</span>
            <div className="text-xs text-gray-500 space-y-2 font-light">
              <p>{CONTACT_INFO.fullAddress}</p>
              <p>{CONTACT_INFO.phone}</p>
              <p>{CONTACT_INFO.email}</p>
            </div>
          </section>
        </div>

        {/* Footer for Print Only */}
        <div className="hidden print:block p-8 border-t border-accent-light mt-auto text-[9px] text-gray-400 text-center italic">
          Dit Curriculum Vitae is gegenereerd via lizamoermancoach.nl • {new Date().toLocaleDateString('nl-NL')}
        </div>
      </div>
      
      <style>{`
        @media print {
          /* Dwing witte achtergrond en verberg web-elementen */
          html, body {
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }

          body * {
            visibility: hidden !important;
          }
          
          /* Maak alleen de modal content zichtbaar */
          .fixed.inset-0, 
          .fixed.inset-0 .relative, 
          .fixed.inset-0 .relative *,
          .fixed.inset-0 #cv-content,
          .fixed.inset-0 #cv-content * {
            visibility: visible !important;
          }

          .fixed.inset-0 {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: auto !important;
            padding: 0 !important;
            margin: 0 !important;
            display: block !important;
            z-index: 1000 !important;
            background: white !important;
          }

          .relative {
            position: relative !important;
            width: 100% !important;
            max-width: 100% !important;
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            max-height: none !important;
            transform: none !important;
          }

          /* Kleuren optimaliseren voor print */
          .text-primary { color: #AC6B6B !important; -webkit-print-color-adjust: exact; }
          .text-accent { color: #D9C0AB !important; -webkit-print-color-adjust: exact; }
          .text-gray-800 { color: #3D3535 !important; -webkit-print-color-adjust: exact; }

          #cv-content {
            padding: 0 !important;
          }
          
          @page {
            size: A4;
            margin: 1.5cm;
          }
        }
      `}</style>
    </div>
  );
};

export default CVModal;
